import React from 'react';
import Icon from '../../../components/AppIcon';

const PagePreviewPanel = ({ content, pageTitle, deviceMode, onDeviceModeChange }) => {
  const deviceModes = [
    { mode: 'desktop', icon: 'Monitor', label: 'Desktop', width: '100%' },
    { mode: 'tablet', icon: 'Tablet', label: 'Tablet', width: '768px' },
    { mode: 'mobile', icon: 'Smartphone', label: 'Mobile', width: '375px' }
  ];

  const currentDevice = deviceModes?.find(d => d?.mode === deviceMode);

  const previewHtml = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>${pageTitle || 'Page Preview'}</title>
      <style>
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 16px;
          line-height: 1.7;
          color: #1a1a2e;
          background: #ffffff;
          padding: 2rem;
          max-width: 860px;
          margin: 0 auto;
        }
        h1, h2, h3, h4, h5, h6 {
          font-weight: 700;
          line-height: 1.3;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: #0f0f1a;
        }
        h1 { font-size: 2rem; }
        h2 { font-size: 1.5rem; }
        h3 { font-size: 1.25rem; }
        p { margin-bottom: 1em; }
        ul, ol { padding-left: 1.5rem; margin-bottom: 1em; }
        li { margin-bottom: 0.25em; }
        a { color: #6366f1; text-decoration: underline; }
        a:hover { color: #4f46e5; }
        strong { font-weight: 600; }
        em { font-style: italic; }
        u { text-decoration: underline; }
        s { text-decoration: line-through; }
        blockquote {
          border-left: 4px solid #6366f1;
          padding: 0.75rem 1rem;
          margin: 1.5rem 0;
          background: #f8f8ff;
          border-radius: 0 8px 8px 0;
          font-style: italic;
          color: #555;
        }
        code {
          background: #f1f1f5;
          padding: 0.15em 0.4em;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 0.9em;
          color: #e11d48;
        }
        pre {
          background: #1e1e2e;
          color: #cdd6f4;
          padding: 1rem 1.25rem;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        pre code { background: none; color: inherit; padding: 0; }
        img { max-width: 100%; height: auto; border-radius: 8px; margin: 1rem 0; }
        hr { border: none; border-top: 2px solid #e5e7eb; margin: 2rem 0; }
        .page-header {
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 1rem;
          margin-bottom: 2rem;
        }
        .page-header .page-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #6366f1;
          margin-bottom: 0.5rem;
        }
        .page-header h1 {
          font-size: 2.25rem;
          margin-top: 0;
          color: #0f0f1a;
        }
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          color: #9ca3af;
          text-align: center;
        }
        .empty-state svg {
          width: 48px;
          height: 48px;
          margin-bottom: 1rem;
          opacity: 0.4;
        }
      </style>
    </head>
    <body>
      ${pageTitle ? `<div class="page-header"><div class="page-label">Page Preview</div><h1>${pageTitle}</h1></div>` : ''}
      ${content
        ? content
        : `<div class="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p style="font-size:1rem;font-weight:500;margin-bottom:0.25rem;">No content yet</p>
            <p style="font-size:0.875rem;">Start typing in the editor to see your page preview here.</p>
          </div>`
      }
    </body>
    </html>
  `;

  return (
    <div className="flex-1 flex flex-col bg-muted min-w-0">
      {/* Preview header */}
      <div className="bg-card border-b border-border px-4 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success" />
          <h3 className="text-sm font-medium text-foreground">Live Preview</h3>
        </div>
        <div className="flex items-center gap-1">
          {deviceModes?.map((device) => (
            <button
              key={device?.mode}
              onClick={() => onDeviceModeChange?.(device?.mode)}
              className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                deviceMode === device?.mode
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              title={device?.label}
              aria-label={device?.label}
            >
              <Icon name={device?.icon} size={16} />
            </button>
          ))}
        </div>
      </div>

      {/* Preview frame */}
      <div className="flex-1 overflow-auto p-4 md:p-6 flex items-start justify-center">
        <div
          className="bg-background rounded-lg shadow-lg transition-all duration-300 overflow-hidden"
          style={{
            width: currentDevice?.width,
            minHeight: '600px',
            maxWidth: '100%',
          }}
        >
          <iframe
            srcDoc={previewHtml}
            title="Page Preview"
            className="w-full border-0"
            style={{ minHeight: '600px', height: '100%' }}
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
};

export default PagePreviewPanel;
